import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
  LinearProgress,
  Divider,
} from '@mui/material';
import PageLayout from './PageLayout';
import { convertJSDayToSystemDay } from '../utils/dateUtils';
import { calculateCurrentStreak } from '../utils/habitUtils';
import { format, addDays, subDays, startOfDay, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';

const PILLARS = [
  { id: 'vitalidade', label: 'Vitalidade' },
  { id: 'mentalidade', label: 'Mentalidade' },
  { id: 'prosperidade', label: 'Prosperidade' },
];

const DEFAULT_PILLAR_COLORS = {
  vitalidade: '#2E7D32',
  mentalidade: '#F9A825',
  prosperidade: '#E65100',
};

const getPillarColor = (pillarId, customColors) => {
  const id = pillarId || 'vitalidade';
  return customColors?.pillars?.[id] || DEFAULT_PILLAR_COLORS[id] || DEFAULT_PILLAR_COLORS.vitalidade;
};

function getHabitStartDate(habit) {
  if (!habit?.startDate) return null;
  const parts = habit.startDate.split('-');
  if (parts.length !== 3) return null;
  const [year, month, day] = parts.map(Number);
  const d = new Date(year, month - 1, day);
  return isNaN(d.getTime()) ? null : d;
}

function getHabitsForToday(habits) {
  const today = new Date();
  const systemDay = convertJSDayToSystemDay(today.getDay());
  return (habits || []).filter((h) => {
    if (h.active === false) return false;
    const start = getHabitStartDate(h);
    if (start && today < startOfDay(start)) return false;
    if (!h.weekdays?.length) return true;
    return h.weekdays.includes(systemDay);
  });
}

function getEventsTodayAndNextDays(events, todayStr, nextDaysSet) {
  if (!events?.length) return { today: [], next: [] };
  const today = [];
  const next = [];
  for (const e of events) {
    const d = e.date instanceof Date ? e.date : (e.date ? new Date(e.date) : null);
    if (!d || isNaN(d.getTime())) continue;
    const dateStr = format(d, 'yyyy-MM-dd');
    if (dateStr === todayStr) today.push(e);
    else if (nextDaysSet.has(dateStr)) next.push(e);
  }
  next.sort((a, b) => {
    const da = a.date instanceof Date ? a.date : new Date(a.date);
    const db = b.date instanceof Date ? b.date : new Date(b.date);
    return da - db;
  });
  return { today, next };
}

function getEventsThisMonth(events) {
  if (!events?.length) return [];
  const now = new Date();
  const start = startOfMonth(now);
  const end = endOfMonth(now);
  const list = [];
  for (const e of events) {
    const d = e.date instanceof Date ? e.date : (e.date ? new Date(e.date) : null);
    if (!d || isNaN(d.getTime())) continue;
    if (d >= start && d <= end) list.push(e);
  }
  list.sort((a, b) => {
    const da = a.date instanceof Date ? a.date : new Date(a.date);
    const db = b.date instanceof Date ? b.date : new Date(b.date);
    return da - db;
  });
  return list;
}

function getWeeklyProgress(habits) {
  const today = new Date();
  const weekStart = subDays(today, 6);
  let totalScheduled = 0;
  let totalCompleted = 0;
  const byPillar = { vitalidade: { s: 0, c: 0 }, mentalidade: { s: 0, c: 0 }, prosperidade: { s: 0, c: 0 } };
  for (let i = 0; i < 7; i++) {
    const d = addDays(weekStart, i);
    const dateStr = format(d, 'yyyy-MM-dd');
    const systemDay = convertJSDayToSystemDay(d.getDay());
    (habits || []).forEach((h) => {
      if (h.active === false) return;
      const start = getHabitStartDate(h);
      if (start && d < startOfDay(start)) return;
      if (h.weekdays?.length && !h.weekdays.includes(systemDay)) return;
      totalScheduled++;
      const pillar = (h.pillar === 'expansao' ? 'mentalidade' : (h.pillar || 'vitalidade'));
      if (byPillar[pillar]) {
        byPillar[pillar].s++;
        const completed = (h.completedDates || []).some((cd) => (cd || '').split('T')[0] === dateStr);
        if (completed) {
          totalCompleted++;
          byPillar[pillar].c++;
        }
      }
    });
  }
  const pctGlobal = totalScheduled > 0 ? Math.round((totalCompleted / totalScheduled) * 100) : 0;
  const pillarPct = {};
  PILLARS.forEach((p) => {
    const { s, c } = byPillar[p.id];
    pillarPct[p.id] = s > 0 ? Math.round((c / s) * 100) : 0;
  });
  const sorted = [...PILLARS].sort((a, b) => (pillarPct[b.id] || 0) - (pillarPct[a.id] || 0));
  const strongest = sorted[0];
  const weakest = sorted[sorted.length - 1];
  return { pctGlobal, pillarPct, strongest: strongest?.id, weakest: weakest?.id };
}

function getUrgentGoal(goals) {
  const withDeadline = (goals || []).filter((g) => !g.completed && g.deadline);
  if (withDeadline.length === 0) return null;
  withDeadline.sort((a, b) => new Date(a.deadline) - new Date(b.deadline));
  return withDeadline[0];
}

function getActiveChallenge(challenges) {
  const today = startOfDay(new Date());
  const active = (challenges || []).find((c) => {
    if (c.completed) return false;
    const start = c.startDate ? startOfDay(new Date(c.startDate)) : null;
    const end = c.endDate ? startOfDay(new Date(c.endDate)) : null;
    if (!start || !end) return false;
    return isWithinInterval(today, { start, end });
  });
  if (!active) return null;
  const duration = parseInt(active.duration, 10) || 0;
  const completedDays = (active.dailyProgress || []).filter((d) => d.completed).length;
  const pct = duration > 0 ? Math.round((completedDays / duration) * 100) : 0;
  return { ...active, progressPct: pct };
}

function formatSleepHours(hours) {
  if (hours == null) return '—';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (m === 0) return `${h}h`;
  return `${h}h${String(m).padStart(2, '0')}`;
}

function qualityStars(n) {
  const v = Math.min(5, Math.max(1, Number(n) || 0));
  return '★'.repeat(v) + '☆'.repeat(5 - v);
}

export default function CentralDashboard({
  habits = [],
  goals = [],
  challenges = [],
  events = [],
  customColors,
  onNavigate,
  onToggleHabit,
  onUpdateHabit,
  userId,
}) {
  const { currentUser } = useAuth();
  const [sleepEntries, setSleepEntries] = useState([]);
  const [userName, setUserName] = useState('');

  const todayStr = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);
  const yesterdayStr = useMemo(() => format(subDays(new Date(), 1), 'yyyy-MM-dd'), []);
  const next7DaysStr = useMemo(() => {
    const set = new Set();
    for (let i = 1; i <= 7; i++) set.add(format(addDays(new Date(), i), 'yyyy-MM-dd'));
    return set;
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!currentUser?.uid) return;
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          const data = snap.data();
          setUserName(data.name || currentUser.displayName || '');
          const entries = (data.sleepEntries || []).map((e) => ({
            ...e,
            quality: typeof e.quality === 'number' ? e.quality : 3,
          }));
          entries.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
          setSleepEntries(entries);
        }
      } catch (err) {
        console.error('Erro ao carregar sono/usuário:', err);
      }
    };
    load();
  }, [currentUser]);

  const sleepYesterday = useMemo(() => sleepEntries.find((e) => e.date === yesterdayStr), [sleepEntries, yesterdayStr]);
  const habitsToday = useMemo(() => getHabitsForToday(habits), [habits]);
  const { today: eventsToday, next: eventsNext } = useMemo(
    () => getEventsTodayAndNextDays(events, todayStr, next7DaysStr),
    [events, todayStr, next7DaysStr]
  );
  const eventsThisMonth = useMemo(() => getEventsThisMonth(events), [events]);
  const weekly = useMemo(() => getWeeklyProgress(habits), [habits]);
  const streak = useMemo(() => calculateCurrentStreak(habits), [habits]);
  const urgentGoal = useMemo(() => getUrgentGoal(goals), [goals]);
  const activeChallenge = useMemo(() => getActiveChallenge(challenges), [challenges]);

  const handleToggleToday = (habit) => {
    if (typeof onToggleHabit !== 'function') return;
    const completed = (habit.completedDates || []).some((d) => (d || '').split('T')[0] === todayStr);
    onToggleHabit(habit.id, todayStr, !completed);
  };

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return 'Bom dia';
    if (h < 18) return 'Boa tarde';
    return 'Boa noite';
  }, []);

  const energyLabel = useMemo(() => {
    if (!sleepYesterday) return null;
    const h = Number(sleepYesterday.hours) || 0;
    const q = Number(sleepYesterday.quality) || 3;
    if (h >= 7 && q >= 4) return 'Alta';
    if (h >= 6 && q >= 3) return 'Média';
    if (h < 6 || q <= 2) return 'Baixa';
    return 'Média';
  }, [sleepYesterday]);

  const insightMessage = useMemo(() => {
    if (sleepYesterday) {
      const h = Number(sleepYesterday.hours) || 0;
      if (h < 6) return `Você dormiu ${formatSleepHours(h)}. Considere reduzir a carga hoje.`;
      if (h >= 7 && (sleepYesterday.quality || 0) >= 4) return 'Sono em dia. Aproveite o dia!';
    } else {
      return 'Você não registrou o sono de ontem.';
    }
    const strongestId = weekly.strongest;
    if (strongestId) {
      const label = PILLARS.find((p) => p.id === strongestId)?.label || strongestId;
      if ((weekly.pillarPct || {})[strongestId] >= 70) return `Sua ${label} está alta esta semana. Continue assim.`;
    }
    return null;
  }, [sleepYesterday, weekly]);

  const reminderMessage = useMemo(() => {
    const mentalidadeHabits = (habits || []).filter((h) => (h.pillar || 'vitalidade') === 'mentalidade' && h.active !== false);
    if (mentalidadeHabits.length === 0) return null;
    for (let i = 0; i < 14; i++) {
      const d = subDays(new Date(), i);
      const dateStr = d.toISOString().split('T')[0];
      const systemDay = convertJSDayToSystemDay(d.getDay());
      const anyDone = mentalidadeHabits.some((h) => (h.completedDates || []).some((cd) => (cd || '').split('T')[0] === dateStr));
      const anyScheduled = mentalidadeHabits.some((h) => !h.weekdays?.length || h.weekdays.includes(systemDay));
      if (anyScheduled && !anyDone) {
        if (i === 0) return 'Hoje você tem hábitos de Mentalidade para marcar.';
        return `Há ${i} dia${i > 1 ? 's' : ''} sem registrar hábitos de Mentalidade.`;
      }
    }
    return null;
  }, [habits]);

  return (
    <PageLayout title="Central" hideAddButton customColors={customColors}>
      <Box sx={{ maxWidth: 720, mx: 'auto', pb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
          {greeting}, {userName || 'usuário'} 👋
        </Typography>

        {/* 1️⃣ Estado Atual */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1.5 }}>
              Como estou hoje?
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography component="span" sx={{ fontSize: '1.1rem' }}>🌙</Typography>
                <Typography variant="body2">
                  Sono de ontem:{' '}
                  {sleepYesterday
                    ? `${formatSleepHours(sleepYesterday.hours)} – ${qualityStars(sleepYesterday.quality)}`
                    : 'Não registrado'}
                </Typography>
              </Box>
              {energyLabel && (
                <Chip size="small" label={`⚡ ${energyLabel}`} color={energyLabel === 'Baixa' ? 'warning' : 'default'} />
              )}
              <Chip size="small" icon={<span>🔥</span>} label={`Streak: ${streak} dias`} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  📊 Semana: {weekly.pctGlobal}%
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* 2️⃣ Foco do Dia */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1.5 }}>
              📌 Hoje
            </Typography>
            {habitsToday.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nenhum hábito programado para hoje.
              </Typography>
            ) : (
              <List dense disablePadding>
                {habitsToday.map((habit) => {
                  const completed = (habit.completedDates || []).some((d) => (d || '').split('T')[0] === todayStr);
                  return (
                    <ListItem key={habit.id} disablePadding disableGutters sx={{ py: 0.25 }}>
                      <ListItemButton dense onClick={() => handleToggleToday(habit)} sx={{ borderRadius: 1, px: 1 }}>
                        <Checkbox
                          size="small"
                          checked={!!completed}
                          onChange={() => handleToggleToday(habit)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{ mr: 1, p: 0.5 }}
                        />
                        <ListItemText
                          primary={habit.name}
                          primaryTypographyProps={{
                            variant: 'body2',
                            sx: { textDecoration: completed ? 'line-through' : 'none', color: completed ? 'text.secondary' : 'text.primary' },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </CardContent>
        </Card>

        {/* 3️⃣ Agenda */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1.5 }}>
              📅 Agenda
            </Typography>
            {eventsToday.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Hoje
                </Typography>
                {eventsToday.map((ev) => (
                  <Typography key={ev.id} variant="body2" sx={{ pl: 1 }}>
                    • {ev.title}
                    {ev.time && ` – ${format(ev.time instanceof Date ? ev.time : new Date(ev.time), 'HH:mm', { locale: ptBR })}`}
                  </Typography>
                ))}
              </Box>
            )}
            {eventsNext.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Próximos 7 dias
                </Typography>
                {eventsNext.slice(0, 7).map((ev) => {
                  const d = ev.date instanceof Date ? ev.date : new Date(ev.date);
                  const dateLabel = format(d, 'dd/MM', { locale: ptBR });
                  const dayName = format(d, 'EEEE', { locale: ptBR });
                  return (
                    <Typography key={ev.id} variant="body2" sx={{ pl: 1 }} component="div">
                      • {dateLabel} – {dayName.charAt(0).toUpperCase() + dayName.slice(1)} – {ev.title}
                    </Typography>
                  );
                })}
              </Box>
            )}
            {eventsThisMonth.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Para este mês
                </Typography>
                {eventsThisMonth.map((ev) => {
                  const d = ev.date instanceof Date ? ev.date : new Date(ev.date);
                  const dateLabel = format(d, 'dd/MM', { locale: ptBR });
                  const dayName = format(d, 'EEEE', { locale: ptBR });
                  return (
                    <Typography key={ev.id} variant="body2" sx={{ pl: 1 }} component="div">
                      • {dateLabel} – {dayName.charAt(0).toUpperCase() + dayName.slice(1)} – {ev.title}
                    </Typography>
                  );
                })}
              </Box>
            )}
            {eventsToday.length === 0 && eventsNext.length === 0 && eventsThisMonth.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Nenhum evento nos próximos dias.
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* 4️⃣ Progresso da Semana */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1.5 }}>
              📈 Semana
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <Typography variant="body2">
                Hábitos: <strong>{weekly.pctGlobal}%</strong>
              </Typography>
              {weekly.strongest && (
                <Typography variant="body2" color="text.secondary">
                  Pilar mais forte: <strong style={{ color: getPillarColor(weekly.strongest, customColors) }}>{PILLARS.find((p) => p.id === weekly.strongest)?.label}</strong>
                </Typography>
              )}
              {weekly.weakest && weekly.weakest !== weekly.strongest && (
                <Typography variant="body2" color="text.secondary">
                  Pilar mais fraco: <strong style={{ color: getPillarColor(weekly.weakest, customColors) }}>{PILLARS.find((p) => p.id === weekly.weakest)?.label}</strong>
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Insights e lembretes */}
        {(insightMessage || reminderMessage || urgentGoal || activeChallenge) && (
          <Card sx={{ mb: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
            <CardContent>
              {insightMessage && (
                <Typography variant="body2" sx={{ mb: reminderMessage || urgentGoal || activeChallenge ? 1 : 0 }}>
                  💡 {insightMessage}
                </Typography>
              )}
              {reminderMessage && (
                <Typography variant="body2" sx={{ mb: urgentGoal || activeChallenge ? 1 : 0 }}>
                  📌 {reminderMessage}
                </Typography>
              )}
              {urgentGoal && (
                <Box sx={{ mb: activeChallenge ? 1 : 0 }}>
                  <Typography variant="body2">
                    🎯 Meta mais urgente: <strong>{urgentGoal.title}</strong>
                    {urgentGoal.deadline && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        (prazo: {format(new Date(urgentGoal.deadline), 'dd/MM', { locale: ptBR })})
                      </Typography>
                    )}
                  </Typography>
                  <Button size="small" onClick={() => onNavigate && onNavigate('goals')} sx={{ mt: 0.5 }}>
                    Ver metas
                  </Button>
                </Box>
              )}
              {activeChallenge && (
                <Box>
                  <Typography variant="body2">
                    🏆 Desafio ativo: <strong>{activeChallenge.name}</strong> – {activeChallenge.progressPct}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={activeChallenge.progressPct}
                    sx={{ mt: 0.5, height: 6, borderRadius: 1 }}
                  />
                  <Button size="small" onClick={() => onNavigate && onNavigate('challenges')} sx={{ mt: 0.5 }}>
                    Ver desafios
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        <Divider sx={{ my: 2 }} />
        
      </Box>
    </PageLayout>
  );
}
